/* eslint-disable no-console */

import { useCallback, useEffect, useRef } from 'react'
import { RudderAnalytics } from '@rudderstack/analytics-js'
import { config } from '../../config'

let rudderAnalyticsInstance: RudderAnalytics | null = null
let isInitialized = false

const initializeRudderStack = async (): Promise<RudderAnalytics | null> => {
  if (typeof window === 'undefined') {
    return null
  }

  if (isInitialized && rudderAnalyticsInstance) {
    // console.log('RudderStack initializeRudderStack: already initialized')
    return rudderAnalyticsInstance
  }

  try {
    const { writeKey, dataPlaneUrl } = config.rudderstack

    if (!writeKey || !dataPlaneUrl) {
      console.error('RudderStack: Missing configuration')
      return null
    }

    rudderAnalyticsInstance = new RudderAnalytics()
    rudderAnalyticsInstance.load(writeKey, dataPlaneUrl, {
      useBeacon: true,
      beaconQueueOptions: {
        maxItems: 1,
        flushQueueInterval: 1
      }
    })

    isInitialized = true
    console.log('RudderStack initialized successfully')

    return rudderAnalyticsInstance
  } catch (error) {
    console.error('RudderStack initialization error:', error)
    return null
  }
}

export interface UseRudderStack {
  identify: (userId: string, traits?: Record<string, any>) => void
  track: (eventName: string, properties?: Record<string, any>) => void
  page: (name?: string, properties?: Record<string, any>) => void
  isReady: boolean
}

export const useRudderStack = (): UseRudderStack => {
  const analyticsRef = useRef<RudderAnalytics | null>(null)

  useEffect(() => {
    const init = async (): Promise<void> => {
      const analytics = await initializeRudderStack()
      analyticsRef.current = analytics
    }

    init()
  }, [])

  const identify = useCallback(
    (userId: string, traits?: Record<string, any>): void => {
      if (!userId) {
        return
      }

      if (analyticsRef.current && isInitialized) {
        try {
          analyticsRef.current.identify(userId, traits)
          // console.log(
          //   'RudderStack identify called successfully for user:',
          //   userId
          // )
        } catch (error) {
          console.error('RudderStack identify error:', error)
        }
      } else {
        console.log('RudderStack not ready for identify call')
      }
    },
    []
  )

  const track = useCallback(
    (eventName: string, properties?: Record<string, any>): void => {
      if (!eventName) {
        console.log('RudderStack track: eventName is required')
        return
      }

      if (analyticsRef.current && isInitialized) {
        try {
          analyticsRef.current.track(eventName, properties)
          // console.log(
          //   'RudderStack track called successfully:',
          //   eventName,
          //   properties
          // )
        } catch (error) {
          console.error('RudderStack track error:', error)
        }
      } else {
        console.log('RudderStack not ready for track call')
      }
    },
    []
  )

  const page = useCallback(
    (name?: string, properties?: Record<string, any>): void => {
      if (analyticsRef.current && isInitialized) {
        try {
          if (name) {
            analyticsRef.current.page(name, properties)
          } else {
            analyticsRef.current.page()
          }
          // console.log('RudderStack page called successfully:', name)
        } catch (error) {
          console.error('RudderStack page error:', error)
        }
      } else {
        console.log('RudderStack not ready for page call')
      }
    },
    []
  )

  return {
    identify,
    track,
    page,
    isReady: isInitialized && !!analyticsRef.current
  }
}
